import { CustomEditor, CustomLink, CustomParagraph, CustomText } from '../../../../models/slate-editor';
import { BaseSelection, Element, Range, Transforms } from 'slate';
import twttr from 'twitter-text';
import { LinkDestination, LinkGoogleCallToAction } from '../../../../models/graphql';
import { BaseLinkSettings } from './onChange';
import { PostEditorLinkToCustomLink } from './withLinks';

const deleteBetweenPoints = (editor: CustomEditor, selection: BaseSelection) => {
  // if we don't have any selection or selection is at one Point, we don't need to delete anything
  if (!selection || Range.isCollapsed(selection)) return;

  // Use Transforms.delete to remove content within the range
  Transforms.delete(editor, { at: selection });
};

const escapeRegExp = (string: string): string => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string

const getChildren = (paragraph: string, existingLink: CustomLink): Array<CustomLink | CustomText> => {
  const { initialUrl } = existingLink;
  const splitReg = new RegExp(`(?=${escapeRegExp(initialUrl)})|(?<=${escapeRegExp(initialUrl)})`, 'g');
  const elems = paragraph.split(splitReg);
  let children: (CustomLink | CustomText)[] = elems.map(e => {
    if (e === initialUrl) {
      return existingLink;
    } else {
      return { text: e };
    }
  });
  const firstElem = children[0];
  const lastElem = children[children.length - 1];
  if (Element.isElement(firstElem) && firstElem.type === 'link') {
    children = [{ text: '' }, ...children];
  } else if (Element.isElement(lastElem) && lastElem.type === 'link') {
    children = [...children, { text: '' }];
  }
  return children;
};

const deserializeHtml = (element: HTMLElement): string => {
  const blockElements = new Set([
    'P',
    'DIV',
    'UL',
    'OL',
    'LI',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'BLOCKQUOTE',
    'PRE',
    'TABLE'
  ]);

  let result = '';

  // Loop through child nodes of the element
  element.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Append text content from text nodes
      result += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const childElement = node as HTMLElement;
      // Recursively handle child elements
      const text = deserializeHtml(childElement);

      if (blockElements.has(childElement.tagName)) {
        // Add a newline after block elements
        result += text + '\n';
      } else {
        // Append inline text without newlines
        result += text;
      }
    }
  });

  // Trim trailing whitespace or newlines and return
  return result;
};

export const withPastedData = (editor: CustomEditor): CustomEditor => {
  const { insertData } = editor;

  editor.insertData = (data: DataTransfer) => {
    let text = data.getData('text/plain'); // Get pasted plain text content
    const html = data.getData('text/html'); // Optionally handle HTML content
    const { selection } = editor;
    deleteBetweenPoints(editor, selection);
    const { link: existingLink } = editor;
    let mappedLink: CustomLink | null;

    if (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      text = deserializeHtml(parsed.body).trim();
    }
    if (text) {
      const paragraphs = text.split(/[\r\n]+/g);
      const fragment: CustomParagraph[] = paragraphs.map(p => {
        if (existingLink) {
          const children = getChildren(p, existingLink);
          return {
            type: 'paragraph',
            children
          };
        } else {
          const links = twttr.extractUrls(p);
          if (links.length) {
            const { postLinkData, linksEnabled } = editor;
            if (!linksEnabled) {
              return {
                type: 'paragraph',
                children: [{ text: p }]
              };
            }
            if (!postLinkData) throw new Error('Base Link Settings is not set.');
            const { defaultBitly, utmTracking, campaignTracking, shouldRemoveUtm } = postLinkData;
            const linkSettings: BaseLinkSettings = {
              destination: LinkDestination.INITIAL,
              useBitly: !!defaultBitly,
              googleCallToAction: LinkGoogleCallToAction.NONE,
              utmTracking: utmTracking,
              addUtm: campaignTracking,
              removeUtm: shouldRemoveUtm,
              removePreview: false,
              linkMetadata: undefined
            };
            const link = links[0];
            const customLink = PostEditorLinkToCustomLink(link, linkSettings);
            if (mappedLink) {
              if (mappedLink.initialUrl !== link) {
                return {
                  type: 'paragraph',
                  children: [{ text: p }]
                };
              }
            } else {
              mappedLink = customLink;
            }
            const children = getChildren(p, customLink);
            return {
              type: 'paragraph',
              children
            };
          } else {
            return {
              type: 'paragraph',
              children: [{ text: p }]
            };
          }
        }
      });

      // Insert text node into the editor at the current selection
      // Transforms.insertFragment
      Transforms.insertFragment(editor, fragment);
      Transforms.move(editor, { unit: 'character', distance: 1 });
    } else {
      insertData(data); // Default behavior
    }
  };

  return editor;
};
